import { IEvent } from "../../../interfaces/models/IEvent";
import { useEventForm } from "../../../hooks/Events/useEventForm";
import LegacyButton from "../../../components/Atomos/Buttons/LegacyButton";
import { updateEvent, getEventById } from "../../../Services/Event";
import { useDispatch } from "react-redux";
import { NewEvent } from "../../../redux/states/event.state";
import DropDownList from "../../../components/Atomos/DropDown/Dropdown";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getEmailTemplates, getEmailTemplateById } from "../../../Services/EmailTemplate";
import { IEmailTemplate } from "../../../interfaces/models/IEmailTemplate";
import { EmailType } from "../../../Enums/EmailEnum";
import { toast } from "react-toastify";
import SaveChanges from "../../../components/Atomos/Icons/EditMode/SaveChanges";
import EmailTemplatePreview from "../../../components/Html/EmailTemplatePreview";

export default function StaffSpecialEventTab({
  event,
  isEditMode,
  onEditClick,
  onEventUpdate,
}: {
  event: IEvent | null;
  isEditMode: boolean;
  onEditClick: () => void;
  onEventUpdate?: (updatedEvent: IEvent) => void;
}) {
  const { event: reduxEvent, updateEventField } = useEventForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currentEvent = reduxEvent || event;

  const [emailTemplates, setEmailTemplates] = useState<IEmailTemplate[]>([]);
  const [isTemplatesLoading, setIsTemplatesLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<{ id: string; name: string } | null>(null);
  const [previewHTML, setPreviewHTML] = useState("");

  useEffect(() => {
    if (!isEditMode || !currentEvent?.id) {
      return;
    }

    let mounted = true;

    const fetchTemplates = async () => {
      setIsTemplatesLoading(true);

      const templates = await getEmailTemplates({
        filter: {
          emailEventId: { value: currentEvent.id, comparisonType: "EQUAL" },
          emailType: { value: "specialEvents" as EmailType, comparisonType: "EQUAL" },
        },
        fields: ["id", "title"],
      });

      if (mounted) {
        setEmailTemplates(templates);
        setIsTemplatesLoading(false);
      }
    };

    fetchTemplates();

    return () => {
      mounted = false;
      setIsTemplatesLoading(false);
    };
  }, [isEditMode, currentEvent?.id]);

  useEffect(() => {
    if (!currentEvent?.specialEventTemplateId && !currentEvent?.specialEventTemplate) {
      setSelectedTemplate(null);
      return;
    }

    const templateId =
      currentEvent.specialEventTemplateId || currentEvent.specialEventTemplate?.id;
    if (!templateId) {
      return;
    }

    const savedTemplate =
      emailTemplates.find((template) => template.id === templateId) ||
      currentEvent.specialEventTemplate;

    if (savedTemplate) {
      setSelectedTemplate({
        id: savedTemplate.id || "",
        name: savedTemplate.title,
      });
    }
  }, [
    currentEvent?.specialEventTemplateId,
    currentEvent?.specialEventTemplate,
    emailTemplates,
  ]);

  const templateOptions = emailTemplates.map((template) => ({
    id: template.id || "",
    name: template.title,
  }));

  const handleTemplateChange = (value: any) => {
    if (value && typeof value === "object" && value.id) {
      setSelectedTemplate(value);
    } else {
      setSelectedTemplate(null);
    }
  };

  const previewTemplateId =
    selectedTemplate?.id ||
    currentEvent?.specialEventTemplateId ||
    currentEvent?.specialEventTemplate?.id ||
    "";

  useEffect(() => {
    if (!previewTemplateId) {
      setPreviewHTML("");
      return;
    }

    let mounted = true;

    const loadPreview = async () => {
      const template = await getEmailTemplateById(previewTemplateId, ["template"]);
      if (mounted) {
        setPreviewHTML(template?.template ?? "");
      }
    };

    loadPreview();

    return () => {
      mounted = false;
    };
  }, [previewTemplateId]);

  const handleSaveTemplate = async () => {
    if (currentEvent && currentEvent.id && selectedTemplate) {
      try {
        const updatedEvent = await updateEvent({
          ...currentEvent,
          specialEventTemplateId: selectedTemplate.id,
        });

        if (updatedEvent) {
          toast.success("Template saved successfully");
          
          const reloadedEvent = await getEventById(currentEvent.id);

          if (reloadedEvent) {
            dispatch(NewEvent(reloadedEvent));
            if (onEventUpdate) {
              onEventUpdate(reloadedEvent);
            }
          }
          onEditClick();
        } else {
          toast.error("Failed to save template");
        }
      } catch (error) {
        console.error("Error saving template:", error);
        toast.error("Error saving template");
      }
    } else if (!selectedTemplate) {
      toast.warning("Please select a template first");
    }
  };

  if (!currentEvent) {
    return <div>Event not found</div>;
  }

  const hasNoMatchingTemplates =
    isEditMode && !isTemplatesLoading && emailTemplates.length === 0;

  if (isEditMode && hasNoMatchingTemplates) {
    return (
      <div className="bg-white p-5 space-y-3 text-left w-full">
        <p className="text-gray-font text-base font-medium">Preview:</p>
        <div className="w-full border border-gray-300 rounded bg-white min-h-[40px]" />
        <p className="text-[#BC3423] text-base">
          No templates found: Please create a template in the Design Email tool to use it here.
        </p>
        <LegacyButton
          text="DESIGN EMAIL"
          style="filled"
          color="accent"
          content="text"
          size="small"
          onClick={() => navigate("/email-design")}
        />
      </div>
    );
  }

  if (isEditMode) {
    return (
      <div className="bg-white p-5 space-y-6">
        <div className="space-y-2 text-left">
          <p className="text-gray-font text-base font-medium">Template:</p>
          <div className="w-full">
            <DropDownList
              title=""
              data={templateOptions}
              selectedObjst={selectedTemplate?.name || ""}
              seter={handleTemplateChange}
              placeholder="Select a template"
              ddClass="w-full"
              getid={true}
            />
          </div>
        </div>
  
        <div className="space-y-2 text-left w-full">
          <p className="text-gray-font text-base font-medium">Preview:</p>
          <EmailTemplatePreview html={previewHTML} />
          <div className="flex justify-center !mt-5">
            <LegacyButton
              text="Save"
              onClick={handleSaveTemplate}
              className="w-full md:w-1/12"
              disabled={!selectedTemplate}
              color="accent"
              content="textIcon"
              style="filled"
              size="small"
              iconOrder="textFirst"
              icon={
                <div className="save-icon-white ml-2">
                  <SaveChanges />
                </div>
              }
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#F6F6F6] pb-4">
    <div className="space-y-2 text-left w-full">
      <p className="text-gray-font text-base font-medium">Preview:</p>
      <EmailTemplatePreview html={previewHTML} />
    </div>
    </div>
  );
}
